import React, { useEffect, useState } from "react";
import { LoadingSpinner } from "../App";
import { Button, RefreshButton } from "../components/ui";
import {
  fetchGoogleSheetExactGrid,
  racksToCSV,
} from "../utils/sheetParser";
import { S3_TWIN_CONFIG } from '../constants';

// ─── Helpers (module-level) ───────────────────────────────────────────────────

const isRackLocationCode = (value) => {
  const text = String(value || "").trim();
  if (!text) return false;
  return /(?:^|[\s/])s\d+\s*[-/]\s*[a-z0-9]+(?:\s*[-/]\s*[a-z0-9]+){1,6}/i.test(text);
};

const getRackDisplayTitle = (rack, rackIndex, sheetGrid) => {
  const baseTitle = rack?.title || rack?.block?.title || "";
  let cleanTitle = String(baseTitle || "").trim();

  if (!cleanTitle || cleanTitle.toLowerCase().startsWith("rack block")) {
    cleanTitle = `Rack ${rackIndex + 1}`;
  }

  if (isRackLocationCode(cleanTitle)) return cleanTitle;

  if (cleanTitle.includes("/")) {
    const parts = cleanTitle.split("/").map((s) => s.trim());
    if (parts.some((part) => isRackLocationCode(part))) return cleanTitle;
  }

  const block = rack?.block;
  if (!block || !sheetGrid || !sheetGrid.length) return cleanTitle;

  const possibleLocations = [];
  const headerRow = block.headerRow || 0;
  const deviceCol = block.deviceCol || 0;
  const rowStart = Math.max(0, headerRow - 6);
  const rowEnd = Math.min(sheetGrid.length - 1, headerRow + 8);
  const colStart = Math.max(0, deviceCol - 10);
  const colEnd = deviceCol + 14;

  for (let row = rowStart; row <= rowEnd; row++) {
    for (let col = colStart; col <= colEnd; col++) {
      const value = String(sheetGrid[row]?.[col] || "").trim();
      if (!value) continue;
      if (isRackLocationCode(value) && !cleanTitle.includes(value)) {
        possibleLocations.push({
          value,
          distance: Math.abs(row - headerRow) + Math.abs(col - deviceCol),
        });
      }
    }
  }

  possibleLocations.sort((a, b) => a.distance - b.distance);
  const location = possibleLocations[0]?.value;
  return location ? `${cleanTitle} / ${location}` : cleanTitle;
};

// ─── Style constants ──────────────────────────────────────────────────────────

const buttonStyle = (active) => ({
  padding: "8px 14px",
  backgroundColor: active ? "#1e40af" : "#e2e8f0",
  color: active ? "#ffffff" : "#334155",
  border: "none",
  borderRadius: "6px",
  fontSize: "12px",
  fontWeight: 700,
  cursor: "pointer",
});

// ─── Sub-components ───────────────────────────────────────────────────────────

function RackTable({ rack, rackIndex, rackUnits, sheetGrid, onCellClick }) {
  const displayTitle = getRackDisplayTitle(rack, rackIndex, sheetGrid);

  const structuralTd = {
    position: "relative",
    height: "1.85%",
    padding: 0,
    borderRight: "1px solid #cbd5e1",
    borderBottom: "1px solid #94a3b8",
    boxSizing: "border-box",
  };

  const absoluteContent = {
    position: "absolute",
    top: 0, bottom: 0, left: 0, right: 0,
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    fontSize: "10px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
  };

  const headerStyle = {
    padding: "2px 4px",
    borderRight: "1px solid #cbd5e1",
    borderBottom: "1px solid #94a3b8",
    fontSize: "10px",
    whiteSpace: "nowrap",
    overflow: "hidden",
  };

  const getSerialNo = (device) =>
    device?.serialNo || device?.slNo || device?.serialNumber || device?.assetNo || "";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "max-content",
        flexShrink: 0,
        height: "100%",
      }}
    >
      <div
        style={{
          backgroundColor: "#1e293b",
          color: "#f8fafc",
          padding: "6px 8px",
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "11px",
          borderTopLeftRadius: "6px",
          borderTopRightRadius: "6px",
          minHeight: "28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
        }}
      >
        {displayTitle}
      </div>

      <div
        style={{
          flex: 1,
          border: "4px solid #1e293b",
          borderTop: "none",
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <table
          style={{
            height: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
            fontSize: "10px",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f8fafc", height: "20px" }}>
              <th style={{ ...headerStyle, width: "40px", textAlign: "center" }}>Power</th>
              <th style={{ ...headerStyle, width: "24px", backgroundColor: "#e2e8f0", textAlign: "center" }}>U</th>
              <th style={{ ...headerStyle, width: "180px" }}>Device Name</th>
              <th style={{ ...headerStyle, width: "24px", backgroundColor: "#e2e8f0", textAlign: "center" }}>U</th>
              <th style={{ ...headerStyle, width: "100px" }}>Serial No</th>
              <th style={{ ...headerStyle, width: "30px", textAlign: "center" }}>PSU1</th>
              <th style={{ ...headerStyle, width: "30px", textAlign: "center" }}>PSU2</th>
              <th style={{ ...headerStyle, width: "100px" }}>Label</th>
            </tr>
          </thead>

          <tbody>
            {rackUnits.map((u) => {
              const deviceIndex = rack.devices.findIndex((device) => device.startU === u);
              const device = deviceIndex !== -1 ? rack.devices[deviceIndex] : null;
              const coveredDevice = rack.devices.find(
                (item) => item.startU > u && item.startU - item.size < u
              );
              const isCovered = Boolean(coveredDevice);
              const rowSpan = device ? device.size : 1;
              const currentRowDevice = device || coveredDevice;

              let rowBg = u % 2 === 0 ? "#ffffff" : "#f8fafc";
              let textColor = "#000000";

              if (currentRowDevice?.completed) {
                rowBg = "#dcfce7";
                textColor = "#000000";
              }

              return (
                <tr key={u} style={{ backgroundColor: rowBg }}>
                  {!isCovered && (
                    <td style={structuralTd} rowSpan={rowSpan} onClick={() => onCellClick(rackIndex, deviceIndex)}>
                      <div style={{ ...absoluteContent, justifyContent: "center", color: textColor, fontWeight: 600 }}>
                        {device?.maxPower || ""}
                      </div>
                    </td>
                  )}

                  <td style={{ ...structuralTd, backgroundColor: "#e2e8f0" }}>
                    <div style={{ ...absoluteContent, justifyContent: "center", fontWeight: 700, color: "#475569", cursor: "default" }}>
                      {u}
                    </div>
                  </td>

                  {!isCovered && (
                    <td style={structuralTd} rowSpan={rowSpan} onClick={() => onCellClick(rackIndex, deviceIndex)}>
                      <div style={{ ...absoluteContent, fontWeight: 600, color: textColor }}>
                        {device?.name || ""}
                      </div>
                    </td>
                  )}

                  <td style={{ ...structuralTd, backgroundColor: "#e2e8f0" }}>
                    <div style={{ ...absoluteContent, justifyContent: "center", fontWeight: 700, color: "#475569", cursor: "default" }}>
                      {u}
                    </div>
                  </td>

                  {!isCovered && (
                    <td style={structuralTd} rowSpan={rowSpan} onClick={() => onCellClick(rackIndex, deviceIndex)}>
                      <div style={{ ...absoluteContent, color: textColor, fontWeight: 600 }}>
                        {getSerialNo(device)}
                      </div>
                    </td>
                  )}

                  {!isCovered && (
                    <td style={structuralTd} rowSpan={rowSpan} onClick={() => onCellClick(rackIndex, deviceIndex)}>
                      <div style={{ ...absoluteContent, justifyContent: "center", color: textColor, fontWeight: 600 }}>
                        {device?.psu1 || ""}
                      </div>
                    </td>
                  )}

                  {!isCovered && (
                    <td style={structuralTd} rowSpan={rowSpan} onClick={() => onCellClick(rackIndex, deviceIndex)}>
                      <div style={{ ...absoluteContent, justifyContent: "center", color: textColor, fontWeight: 600 }}>
                        {device?.psu2 || ""}
                      </div>
                    </td>
                  )}

                  {!isCovered && (
                    <td style={structuralTd} rowSpan={rowSpan} onClick={() => onCellClick(rackIndex, deviceIndex)}>
                      <div style={{ ...absoluteContent, color: textColor, fontWeight: 600, backgroundColor: "transparent" }}>
                        {device?.label || ""}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          height: "8px",
          backgroundColor: "#0f172a",
          borderRadius: "0 0 4px 4px",
          marginTop: "2px",
          flexShrink: 0,
        }}
      />
    </div>
  );
}

function CompletionModal({ racks, editingCell, onMarkCompleted, onClose }) {
  const device = racks[editingCell?.rackIndex]?.devices[editingCell?.deviceIndex];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(2px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          padding: "24px",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
          maxWidth: "420px",
          width: "90%",
          zIndex: 1001,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: "0 0 16px 0", color: "#1e40af", fontSize: "16px", fontWeight: 700 }}>
          Mark Device Completion
        </h2>

        <p style={{ margin: "0 0 8px 0", color: "#334155", fontSize: "13px" }}>
          <strong>Device:</strong> {device?.name || "-"}
        </p>
        <p style={{ margin: "0 0 8px 0", color: "#334155", fontSize: "13px" }}>
          <strong>U Position:</strong> {device?.startU || "-"}
        </p>
        <p style={{ margin: "0 0 8px 0", color: "#334155", fontSize: "13px" }}>
          <strong>Serial No:</strong>{" "}
          {device?.serialNo || device?.slNo || device?.serialNumber || device?.assetNo || "-"}
        </p>
        <p style={{ margin: "0 0 20px 0", color: "#64748b", fontSize: "13px" }}>
          Is this row completed?
        </p>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            onClick={() => onMarkCompleted(true)}
            style={{ flex: 1, minWidth: "120px", padding: "10px", backgroundColor: "#16a34a", color: "#ffffff", border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}
          >
            Yes, Completed
          </button>
          <button
            onClick={() => onMarkCompleted(false)}
            style={{ flex: 1, minWidth: "120px", padding: "10px", backgroundColor: "#e5e7eb", color: "#374151", border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}
          >
            Not Completed
          </button>
          <button
            onClick={onClose}
            style={{ padding: "10px 14px", backgroundColor: "#f1f5f9", color: "#475569", border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function RackElevationWithSheets() {
  const GOOGLE_SHEET_ID =
  S3_TWIN_CONFIG.SHEET_ID;

const SHEET_GID =
  S3_TWIN_CONFIG.RACK_ELEVATION_GID;

const AUTO_REFRESH_INTERVAL =
  S3_TWIN_CONFIG.REFRESH_INTERVAL_MS;

  const [loading, setLoading] = useState(true);
  const [racks, setRacks] = useState([]);
  const [sheetGrid, setSheetGrid] = useState([]);
  const [cellMeta, setCellMeta] = useState({});
  const [detectedBlocks, setDetectedBlocks] = useState([]);
  const [viewMode, setViewMode] = useState("sheet");
  const [editingCell, setEditingCell] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const maxRackU =
    racks.length > 0
      ? Math.max(45, ...racks.flatMap((rack) => rack.devices.map((d) => d.startU)))
      : 45;

  const rackUnits = Array.from({ length: maxRackU }, (_, i) => maxRackU - i);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const parsed = await fetchGoogleSheetExactGrid(GOOGLE_SHEET_ID, SHEET_GID);

      setSheetGrid(parsed.grid || []);

      const normalizedRacks = (parsed.racks || []).map((rack) => ({
        ...rack,
        devices: (rack.devices || []).map((device) => ({ ...device, completed: false })),
      }));

      setRacks(normalizedRacks);
      setDetectedBlocks(parsed.detectedBlocks || []);
      setCellMeta(parsed.cellMeta || {});
      setLastUpdated(new Date());

      if (!parsed.grid?.length) {
        setError("Google Sheet loaded, but no grid data was found.");
      } else if (!parsed.racks?.length) {
        setError("Sheet grid loaded, but no rack devices were extracted.");
      }
    } catch (err) {
      setError(`Cannot load Google Sheet: ${err.message}`);
      setSheetGrid([]);
      setRacks([]);
      setDetectedBlocks([]);
      setCellMeta({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = AUTO_REFRESH_INTERVAL ? setInterval(loadData, AUTO_REFRESH_INTERVAL) : null;
    return () => { if (interval) clearInterval(interval); };
  }, []);

  const handleCellClick = (rackIndex, deviceIndex) => {
    if (deviceIndex < 0) return;
    const device = racks[rackIndex]?.devices[deviceIndex];
    if (!device) return;
    setEditingCell({ rackIndex, deviceIndex });
  };

  const handleMarkCompleted = (completed) => {
    if (!editingCell) return;
    setRacks((prev) =>
      prev.map((rack, ri) =>
        ri !== editingCell.rackIndex ? rack : {
          ...rack,
          devices: rack.devices.map((device, di) =>
            di !== editingCell.deviceIndex ? device : { ...device, completed }
          ),
        }
      )
    );
    setEditingCell(null);
  };

  const handleExportData = () => {
    if (!racks.length) { alert("No rack data to export."); return; }
    const csv = racksToCSV(racks);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rack-elevation-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getExactCellStyle = (rowIndex, colIndex, cell) => {
    const meta = cellMeta[`${rowIndex}:${colIndex}`] || {};
    const value = String(cell || "").trim();

    let backgroundColor = "#ffffff";
    let color = "#111827";
    let fontWeight = 400;

    if (meta.rackTitle) { backgroundColor = "#1e293b"; color = "#ffffff"; fontWeight = 700; }
    else if (meta.uCell) { backgroundColor = "#e2e8f0"; fontWeight = 700; }
    else if (meta.rackDeviceArea && value) { backgroundColor = "#dbeafe"; fontWeight = 600; }
    else if (meta.labelArea && value) { backgroundColor = "#dcfce7"; color = "#15803d"; fontWeight = 700; }
    else if (meta.psuArea && value) { backgroundColor = "#f8fafc"; }
    else if (meta.powerArea && value && value !== "0") { backgroundColor = "#fff7ed"; fontWeight = 600; }

    return {
      minWidth: "82px",
      maxWidth: "180px",
      height: "24px",
      border: "1px solid #cbd5e1",
      padding: "2px 6px",
      backgroundColor,
      color,
      fontWeight,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: "11px",
      boxSizing: "border-box",
    };
  };

  if (loading) {
    return (
      <div
        style={{
          height: "100%",               // ← fits within routes wrapper
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f1f5f9",
        }}
      >
        <LoadingSpinner />
        <p style={{ marginTop: "16px", color: "#64748b", fontSize: "14px" }}>
          Loading rack data from Google Sheet...
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        height: "100%",                 // ← fits within routes wrapper, no fixed positioning
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f1f5f9",
        overflow: "hidden",
      }}
    >
      {/* Toolbar — replaces old renderHeader with its h1 title */}
      <div
        style={{
          flexShrink: 0,
          padding: "10px 24px",
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <p style={{ margin: 0, color: "#64748b", fontSize: "12px" }}>
          {lastUpdated && `Last updated: ${lastUpdated.toLocaleTimeString()} · `}
          {`Blocks: ${detectedBlocks.length} · Racks: ${racks.length}`}
        </p>

        {error && (
          <div
            style={{
              padding: "8px 12px",
              backgroundColor: "#fee2e2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              color: "#991b1b",
              fontSize: "12px",
              whiteSpace: "pre-wrap",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button onClick={() => setViewMode("sheet")} style={buttonStyle(viewMode === "sheet")}>
            Sheet View
          </button>
          <button onClick={() => setViewMode("rack")} style={buttonStyle(viewMode === "rack")}>
            Rack View
          </button>
          <RefreshButton onClick={loadData} refreshing={loading} size="sm" />
          <Button variant="navy" size="sm" onClick={handleExportData} disabled={!racks.length}>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Content */}
      {!sheetGrid.length ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px",
            textAlign: "center",
            backgroundColor: "#f1f5f9",
          }}
        >
          <div style={{ color: "#64748b", fontSize: "48px", marginBottom: "16px" }}>📭</div>
          <h2 style={{ margin: "0 0 8px 0", color: "#1e40af", fontSize: "18px" }}>
            No Sheet Data Found
          </h2>
          <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>
            Check Sheet ID, GID, sharing access, and CSV export.
          </p>
          <Button variant="primary" onClick={loadData} style={{ marginTop: 24 }}>
            Try Again
          </Button>
        </div>
      ) : viewMode === "sheet" ? (
        <div style={{ flex: 1, overflow: "auto", padding: "12px 24px", backgroundColor: "#f1f5f9" }}>
          <table style={{ borderCollapse: "collapse", backgroundColor: "#ffffff", fontSize: "11px" }}>
            <tbody>
              {sheetGrid.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, colIndex) => {
                    const meta = cellMeta[`${rowIndex}:${colIndex}`] || {};
                    return (
                      <td
                        key={`${rowIndex}-${colIndex}`}
                        title={meta.blockTitle || cell || ""}
                        style={getExactCellStyle(rowIndex, colIndex, cell)}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            padding: "10px 24px",
            overflowX: "auto",
            overflowY: "hidden",
            display: "flex",
            gap: "32px",
            alignItems: "stretch",
            backgroundColor: "#f1f5f9",
          }}
        >
          {racks.map((rack, rackIndex) => (
            <RackTable
              key={`${rack.title}-${rackIndex}`}
              rack={rack}
              rackIndex={rackIndex}
              rackUnits={rackUnits}
              sheetGrid={sheetGrid}
              onCellClick={handleCellClick}
            />
          ))}
        </div>
      )}

      {editingCell && (
        <CompletionModal
          racks={racks}
          editingCell={editingCell}
          onMarkCompleted={handleMarkCompleted}
          onClose={() => setEditingCell(null)}
        />
      )}
    </div>
  );
}

export default RackElevationWithSheets;